import { normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, Fragment as _Fragment, createVNode as _createVNode } from "vue";
// Styles
import "./VProgressCircular.css";

// Components
import { circularWavePath, hasWave } from "../VProgressLinear/waves.js"; // Composables
import { useTextColor } from "../../composables/color.js";
import { makeComponentProps } from "../../composables/component.js";
import { useElementIntersection } from "../../composables/intersectionObserver.js";
import { useElementSize } from "../../composables/resizeObserver.js";
import { makeRevealProps, useReveal } from "../../composables/reveal.js";
import { makeSizeProps, useSize } from "../../composables/size.js";
import { makeTagProps } from "../../composables/tag.js";
import { makeThemeProps, provideTheme } from "../../composables/theme.js"; // Utilities
import { computed, shallowRef, toRef } from 'vue';
import { clamp, convertToUnit, genericComponent, isObject, PREFERS_REDUCED_MOTION, propsFactory, useRender } from "../../util/index.js"; // Types
// in px, scaled to the viewBox
const WAVE_AMPLITUDE = 1.6;
const WAVELENGTH = 15;
const WAVE_GAP = 4;
export const makeVProgressCircularProps = propsFactory({
  bgColor: String,
  color: String,
  indeterminate: [Boolean, String],
  rounded: Boolean,
  modelValue: {
    type: [Number, String],
    default: 0
  },
  rotate: {
    type: [Number, String],
    default: 0
  },
  shape: {
    type: String,
    default: 'flat',
    validator: v => ['flat', 'wavy'].includes(v)
  },
  variant: {
    type: String,
    default: undefined,
    validator: v => ['split'].includes(v)
  },
  transition: {
    type: [Boolean, Object],
    default: undefined
  },
  width: {
    type: [Number, String],
    default: 4
  },
  ...makeComponentProps(),
  ...makeRevealProps(),
  ...makeSizeProps(),
  ...makeTagProps({
    tag: 'div'
  }),
  ...makeThemeProps()
}, 'VProgressCircular');
export const VProgressCircular = genericComponent()({
  name: 'VProgressCircular',
  props: makeVProgressCircularProps(),
  setup(props, {
    slots
  }) {
    const MAGIC_RADIUS_CONSTANT = 20;
    const CIRCUMFERENCE = 2 * Math.PI * MAGIC_RADIUS_CONSTANT;
    const root = shallowRef();
    const {
      themeClasses
    } = provideTheme(props);
    const {
      sizeClasses,
      sizeStyles
    } = useSize(props);
    const {
      textColorClasses,
      textColorStyles
    } = useTextColor(() => props.color);
    const {
      textColorClasses: underlayColorClasses,
      textColorStyles: underlayColorStyles
    } = useTextColor(() => props.bgColor);
    const {
      width: containerWidth
    } = useElementSize(root);
    const {
      isIntersecting
    } = useElementIntersection(root);
    const {
      state: revealState,
      duration: revealDuration
    } = useReveal(props);
    const normalizedValue = toRef(() => revealState.value === 'initial' ? 0 : clamp(parseFloat(props.modelValue), 0, 100));
    const width = toRef(() => Number(props.width));
    const transitionDuration = toRef(() => props.transition === false ? undefined : convertToUnit(isObject(props.transition) ? props.transition.duration : undefined, 'ms'));
    const size = toRef(() => {
      // Get size from element if size prop value is small, large etc
      return sizeStyles.value ? Number(props.size) : containerWidth.value ? containerWidth.value : Math.max(width.value, 32);
    });
    const diameter = toRef(() => MAGIC_RADIUS_CONSTANT / (1 - width.value / size.value) * 2);
    const strokeWidth = toRef(() => width.value / size.value * diameter.value);
    const strokeDashOffset = toRef(() => {
      const baseLength = (100 - normalizedValue.value) / 100 * CIRCUMFERENCE;
      return props.rounded && normalizedValue.value > 0 && normalizedValue.value < 100 ? convertToUnit(Math.min(CIRCUMFERENCE - 0.01, baseLength + strokeWidth.value)) : convertToUnit(baseLength);
    });
    const startAngle = computed(() => {
      const baseAngle = Number(props.rotate);
      return props.rounded ? baseAngle + strokeWidth.value / 2 / CIRCUMFERENCE * 360 : baseAngle;
    });
    const isWavy = toRef(() => props.shape === 'wavy');
    const geometry = computed(() => {
      if (props.variant !== 'split' && !isWavy.value) return undefined;
      const scale = diameter.value / size.value;
      const radius = MAGIC_RADIUS_CONSTANT;
      const amplitude = isWavy.value && (props.indeterminate || hasWave(normalizedValue.value)) ? WAVE_AMPLITUDE * scale : 0;
      const waves = Math.max(3, Math.floor(CIRCUMFERENCE / (WAVELENGTH * scale)));
      const center = diameter.value / 2;
      const arc = `A ${radius} ${radius} 0 1 1`;
      const roundCaps = props.rounded || isWavy.value;
      return {
        waves,
        linecap: roundCaps ? 'round' : 'butt',
        // round caps reach half a stroke into the gap from each side
        gap: (WAVE_GAP * scale + (roundCaps ? strokeWidth.value : 0)) / CIRCUMFERENCE * 100,
        // two turns so the indeterminate track can wrap past the start
        track: `M ${center + radius} ${center}` + ` ${arc} ${center - radius} ${center} ${arc} ${center + radius} ${center}`.repeat(2),
        // peaks sit on the track and troughs dip inward, so a flat wave lands on the track
        wave: circularWavePath(center, radius - amplitude, amplitude, waves)
      };
    });
    useRender(() => _createVNode(props.tag, {
      "ref": root,
      "class": _normalizeClass(['v-progress-circular', {
        'v-progress-circular--indeterminate': !!props.indeterminate,
        'v-progress-circular--visible': isIntersecting.value,
        'v-progress-circular--variant-split': props.variant === 'split',
        'v-progress-circular--shape-wavy': isWavy.value,
        'v-progress-circular--disable-shrink': props.indeterminate && (props.indeterminate === 'disable-shrink' || PREFERS_REDUCED_MOTION()),
        'v-progress-circular--revealing': ['initial', 'pending'].includes(revealState.value),
        'v-progress-circular--no-transition': props.transition === false
      }, themeClasses.value, sizeClasses.value, textColorClasses.value, props.class]),
      "style": _normalizeStyle([sizeStyles.value, textColorStyles.value, {
        '--v-progress-reveal-duration': `${revealDuration.value}ms`,
        '--v-progress-circular-transition-duration': transitionDuration.value
      }, props.style]),
      "role": "progressbar",
      "aria-valuemin": "0",
      "aria-valuemax": "100",
      "aria-valuenow": props.indeterminate ? undefined : normalizedValue.value
    }, {
      default: () => [_createElementVNode("svg", {
        "style": {
          transform: `rotate(calc(-90deg + ${startAngle.value}deg))`
        },
        "xmlns": "http://www.w3.org/2000/svg",
        "viewBox": `0 0 ${diameter.value} ${diameter.value}`
      }, [geometry.value ? _createElementVNode("g", {
        "class": "v-progress-circular__split",
        "style": {
          '--v-progress-circular-value': normalizedValue.value,
          '--v-progress-circular-stroke': convertToUnit(strokeWidth.value),
          '--v-progress-circular-waves': geometry.value.waves,
          '--v-progress-circular-gap': geometry.value.gap
        }
      }, [_createElementVNode("path", {
        "class": _normalizeClass(['v-progress-circular__underlay', 'v-progress-circular__track', underlayColorClasses.value]),
        "style": _normalizeStyle(underlayColorStyles.value),
        "d": geometry.value.track,
        "pathLength": "200",
        "stroke-linecap": geometry.value.linecap
      }, null), _createElementVNode("path", {
        "class": "v-progress-circular__wave",
        "style": {
          d: `path("${geometry.value.wave}")`
        },
        "d": geometry.value.wave,
        "pathLength": 100 + 100 / geometry.value.waves,
        "stroke-linecap": geometry.value.linecap
      }, null)]) : _createElementVNode(_Fragment, null, [_createElementVNode("circle", {
        "class": _normalizeClass(['v-progress-circular__underlay', underlayColorClasses.value]),
        "style": _normalizeStyle(underlayColorStyles.value),
        "fill": "transparent",
        "cx": "50%",
        "cy": "50%",
        "r": MAGIC_RADIUS_CONSTANT,
        "stroke-width": strokeWidth.value,
        "stroke-dasharray": CIRCUMFERENCE,
        "stroke-dashoffset": 0
      }, null), _createElementVNode("circle", {
        "class": "v-progress-circular__overlay",
        "fill": "transparent",
        "cx": "50%",
        "cy": "50%",
        "r": MAGIC_RADIUS_CONSTANT,
        "stroke-width": strokeWidth.value,
        "stroke-dasharray": CIRCUMFERENCE,
        "stroke-dashoffset": strokeDashOffset.value,
        "stroke-linecap": props.rounded ? 'round' : undefined
      }, null)])]), slots.default && _createElementVNode("div", {
        "class": "v-progress-circular__content"
      }, [slots.default({
        value: normalizedValue.value
      })])]
    }));
    return {};
  }
});
//# sourceMappingURL=VProgressCircular.js.map